import { MDXProvider } from "@mdx-js/react";
import { Accordion, AccordionSummary } from "@mui/material";
import { MDXRemote } from "next-mdx-remote";
import ChevronDown from "public/images/icons/chevron-down.svg";
import { useEffect, useMemo, useState } from "react";
import { convertMDX, mdxFaqComponent } from "utils/mdx";

import useSearchWithBreadCrumbs from "@/components/brandRefresh/common/use-search-with-breadcrumbs";
import { containsIgnoreCase } from "utils/new-helper";

import styles from "@/styles/legal-rerfesh.module.scss";

const searchFilterCallback = (questions, search) => {
  return questions.filter(
    ({ Question, Answer }) =>
      containsIgnoreCase(Question, search) ||
      containsIgnoreCase(Answer, search),
  );
};

const FAQQuestionsView = ({ questions, faqTopics }) => {
  const [summary, setSummary] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState();

  useEffect(() => {
    const hash = window.location.hash.replace(/^#question_/, "");
    if (hash) {
      setExpandedQuestion(questions.find(q => String(q.id) === hash));
    }
  }, []);

  const breadcrumbTree = useMemo(
    () => [
      {
        title: "Help & Support",
        url: "/help",
        children: [
          {
            title: "FAQs",
            url: "/help/faq",
            children: faqTopics.map(({ attributes }) => ({
              title: attributes.Title,
              url: `/help/faq/${attributes.slug}`,
            })),
          },
        ],
      },
    ],
    [faqTopics],
  );

  useEffect(() => {
    async function setData() {
      const source = expandedQuestion
        ? expandedQuestion.Answer.replace("<br>", " ")
        : "";
      const mdxSource = await convertMDX(source, false);
      setSummary(mdxSource);
    }
    setData();
  }, [expandedQuestion]);

  const DocsAccordion = ({ children, ...props }) => {
    return (
      <Accordion
        disableGutters
        elevation={0}
        {...props}
        className="!bg-onmoPurple-50 p-6 lg:px-10 py-3 !text-onmoPrimary text-base lg:text-xl font-medium before:hidden m-0 !rounded-lg !first:rounded-t-lg !last:rounded-b-lg"
      >
        {children}
      </Accordion>
    );
  };

  const DocsAccordionSummary = ({ children, ...props }) => {
    return (
      <AccordionSummary {...props} className="!p-0 min-h-0 before:hidden">
        <div className={`flex items-center m-0`}>{children}</div>
      </AccordionSummary>
    );
  };
  const handleSubAccordionChange = panel => (event, selectedQuestion) =>
    setExpandedQuestion(selectedQuestion ? panel : undefined);

  const { renderSearchView, filteredValues: filteredQuestions } =
    useSearchWithBreadCrumbs({
      breadcrumbTree,
      defaultValues: questions,
      searchFilterCallback,
      showAutoCompleteDropDown: false,
    });

  return (
    <>
      {renderSearchView()}
      <div className="flex flex-col gap-4">
        {filteredQuestions?.map((q, l) => (
          <DocsAccordion
            key={l}
            id={`question_${q.id}`}
            expanded={expandedQuestion?.id === q.id}
            onChange={handleSubAccordionChange(q)}
          >
            <DocsAccordionSummary
              expandIcon={
                <span className="text-onmoPrimary p-2">
                  <ChevronDown />
                </span>
              }
              aria-controls={`panel${l}`}
              id={`panel${l}`}
            >
              <p>{q.Question}</p>
            </DocsAccordionSummary>
            {summary && (
              <div className="text-onmoPrimaryBlack font-normal rounded-b-lg p-2 pl-0 py-3 text-base 4xl:text-xl">
                <MDXProvider components={mdxFaqComponent}>
                  <div className={styles.mdx}>
                    <MDXRemote {...summary} />
                  </div>
                </MDXProvider>
              </div>
            )}
          </DocsAccordion>
        ))}
      </div>
    </>
  );
};

export default FAQQuestionsView;
